import {useState, type ReactNode} from 'react'
import {Popover, PopoverContent, PopoverTrigger} from '@/components/ui/popover'
import {AREA_TACTIL} from '@/components/ui/info-tip'

/** Procedencia bajo demanda (T6/T7, `docs/doctrina/ux-texto-spec.md`): popover y no tooltip porque se lee,
 * se copia y se abre en un teléfono. */
export function PorQue({
  label,
  ariaLabel,
  titulo,
  children,
  testId,
  contenidoTestId,
  contenedor,
  siempreVisible = false,
  enFlujo = false,
}: {
  label: string
  /** Nombre accesible cuando el rótulo se repite fila a fila (T11): «¿Por qué? — {dato}». */
  ariaLabel?: string
  titulo?: string
  children: ReactNode
  testId?: string
  contenidoTestId?: string
  /** Dentro de un diálogo modal, el nodo del `DialogContent` (ver `popover.tsx`). */
  contenedor?: HTMLElement | null
  /** En línea, sin popover (T14): el popover cerrado no está en el DOM para Ctrl+F ni la impresión. */
  siempreVisible?: boolean
  /** El detalle empuja en vez de flotar: en un diálogo con acciones abajo, el popover las taparía.
   * Escape no se intercepta: Radix lo escucha en `document` en fase de captura, antes que React. */
  enFlujo?: boolean
}) {
  const [abierto, setAbierto] = useState(false)
  const cuerpo = (
    <>
      {titulo && (
        <p className="font-mono text-[10px] font-medium tracking-wider text-muted-foreground uppercase">{titulo}</p>
      )}
      {children}
    </>
  )
  if (enFlujo) {
    return (
      <div className="space-y-1.5">
        <button
          type="button"
          aria-expanded={abierto}
          aria-label={ariaLabel ?? label}
          data-testid={testId}
          onClick={() => setAbierto((previo) => !previo)}
          className={`text-xs text-muted-foreground underline underline-offset-2 outline-none hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring/50 ${AREA_TACTIL}`}>
          {label}
        </button>
        {abierto && (
          <div className="space-y-1.5 text-xs text-muted-foreground" data-testid={contenidoTestId}>
            {cuerpo}
          </div>
        )}
      </div>
    )
  }
  if (siempreVisible) {
    return (
      <div className="space-y-1.5 text-xs text-muted-foreground" data-testid={contenidoTestId}>
        {cuerpo}
      </div>
    )
  }
  return (
    <Popover>
      <PopoverTrigger
        type="button"
        aria-label={ariaLabel ?? label}
        data-testid={testId}
        className={`text-xs text-muted-foreground underline underline-offset-2 outline-none hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring/50 ${AREA_TACTIL}`}>
        {label}
      </PopoverTrigger>
      <PopoverContent
        container={contenedor}
        aria-label={ariaLabel ?? titulo ?? label}
        className="max-w-[22rem] space-y-1.5 p-3 text-xs text-popover-foreground"
        data-testid={contenidoTestId}>
        {cuerpo}
      </PopoverContent>
    </Popover>
  )
}
